import { useCallback, useEffect, useState } from 'react'
import { Percent, Plus } from 'lucide-react'

import { CommissionRuleBuilder } from '@/components/masters/CommissionRuleBuilder'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createCommissionProfile,
  fetchCommissionProfiles,
  updateCommissionProfile,
} from '@/lib/api/commissionProfiles'
import {
  commissionProfileFormToPayload,
  commissionProfileToForm,
  emptyCommissionProfileForm,
  ensureDefaultRule,
} from '@/lib/masters/commissionProfileMaster'
import { cn } from '@/lib/utils'

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

export function CommissionProfilesPanel() {
  const { activeBranchId } = useBranch()
  const [profiles, setProfiles] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyCommissionProfileForm())
  const [submitError, setSubmitError] = useState(null)

  const loadProfiles = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const items = await fetchCommissionProfiles()
      setProfiles(items)
      return items
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to load commission profiles.')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true
    setSuccess('')
    setError('')

    async function init() {
      const items = await loadProfiles()
      if (!active) return

      if (items.length === 0) {
        setCreating(true)
        setSelectedId(null)
        setForm(emptyCommissionProfileForm())
        return
      }

      const preferred = items[0]
      setSelectedId(preferred.id)
      setForm(commissionProfileToForm(preferred))
      setCreating(false)
    }

    init()
    return () => {
      active = false
    }
  }, [activeBranchId, loadProfiles])

  function selectProfile(profile) {
    setCreating(false)
    setSelectedId(profile.id)
    setForm(commissionProfileToForm(profile))
    setSuccess('')
    setError('')
    setSubmitError(null)
  }

  function startCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyCommissionProfileForm())
    setSuccess('')
    setError('')
    setSubmitError(null)
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')
    setSubmitError(null)

    const payload = commissionProfileFormToPayload(ensureDefaultRule(form))

    try {
      if (creating) {
        const created = await createCommissionProfile(payload)
        const items = await loadProfiles()
        const saved = items.find((item) => item.id === created.id) ?? created
        selectProfile(saved)
        setSuccess('Commission profile created.')
      } else if (selectedId) {
        const updated = await updateCommissionProfile(selectedId, payload)
        setProfiles((current) =>
          current.map((item) => (item.id === updated.id ? updated : item)),
        )
        setForm(commissionProfileToForm(updated))
        setSuccess('Commission profile saved.')
      }
    } catch (err) {
      setSubmitError(err)
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'rules') ??
        err?.response?.data?.message ??
        'Unable to save commission profile.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="card-surface p-3">
        <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Profiles
        </p>
        {loading ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Loading…</p>
        ) : profiles.length === 0 && !creating ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">No profiles yet.</p>
        ) : (
          <div className="space-y-1">
            {profiles.map((profile) => {
              const isActive = !creating && selectedId === profile.id
              return (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => selectProfile(profile)}
                  className={cn(
                    'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm transition-colors',
                    isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/80',
                  )}
                >
                  <span className="truncate font-medium">{profile.name}</span>
                  <span className="text-[11px] text-muted-foreground">{profile.summary}</span>
                </button>
              )
            })}
            {creating ? (
              <div className="rounded-xl bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
                New profile
              </div>
            ) : null}
          </div>
        )}
        <Button type="button" variant="outline" className="mt-3 w-full" onClick={startCreate}>
          <Plus className="h-4 w-4" />
          New profile
        </Button>
      </aside>

      <form onSubmit={handleSubmit} className="card-surface p-6 lg:p-8">
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Percent className="h-4 w-4" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Commission profile</h2>
            <p className="text-sm text-muted-foreground">
              Define default and override rates, then assign profiles on staff records.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="profile-name" className="mb-1.5 block text-sm font-medium">
                Name
              </label>
              <input
                id="profile-name"
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm"
                required
              />
            </div>
            <div>
              <label htmlFor="profile-description" className="mb-1.5 block text-sm font-medium">
                Description
              </label>
              <input
                id="profile-description"
                type="text"
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                className="h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => updateField('is_default', e.target.checked)}
              className="rounded border-border"
            />
            Default profile for new stylists
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-border"
            />
            Profile is active
          </label>

          <CommissionRuleBuilder
            form={form}
            onChange={setForm}
            error={submitError}
          />

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}

          <Button type="submit" disabled={submitting || loading}>
            {submitting ? 'Saving…' : creating ? 'Create profile' : 'Save profile'}
          </Button>
        </div>
      </form>
    </div>
  )
}
